/**
 * Date picker (CHANGE 2026-10-18 §2.1; look and behaviour of the prototype, date-picker change of 18 Oct 2026).
 * Three forms of one component:
 *  - inline:  desktop, 2 months side by side (max ~700 px, centred), previous / next at the outer edges;
 *  - popover: the same in a panel (compact search);
 *  - sheet:   mobile (< 640 px), full screen, ONE month with previous / next (0.9.5; the whole window stacked
 *             vertically before), sticky header with the dates and « Valider ».
 * Once an arrival is chosen, « Séjour minimum : N nuits » under the header (1.7.3, every form). Under the months:
 * the hint line, the legend (a struck sample day + the rooms' minimum stays sent by the
 * server) and, inline / popover, « Réinitialiser ». First month shown (0.9.6): the arrival's (URL arrivee), else
 * the URL parameter mois, else the shortcode attribute mois, else the default month computed by the server
 * (/start config.calendar.defaultMonth: today + the Règles lead, months without a Beds24 arrival day skipped), else the current month — kept
 * inside the window (‹ still goes back to the current month).
 *
 * Every state comes from the server: arrival days and their reasons (/calendar), valid departures and the
 * minimum nights (/calendar/departures: departures start at firstDeparture — no 1-night stay can be chosen, 1.7.3,
 * 01 §19 exception), the legend's minimum stays (/calendar minStay). No stay rule is computed here; a day without a
 * server reason shows no hint. A day before firstDeparture: the day after the arrival carries « min. N nuits »; a click
 * on one only repeats the minimum (no search, no contact).
 *
 * Day = <button class="pap-day"><span class="pap-day-n">12</span></button>; the nights bubble
 * (<span class="pap-day-tip">) is absolutely positioned above the departure (chosen, hovered or focused), out of
 * the cell's flow, so the number never moves; « min. N nuits » (<span class="pap-day-min">) sits there the same way.
 * Unselectable days (past, closed — no room available in Beds24 —, not a departure the server sent)
 * share one look (.is-off: muted, struck); the state classes stay for styling.
 *
 * Keyboard: arrows (day / week), PageUp / PageDown (month), Home / End (week), Enter / Space (select). Every
 * day keeps a full-date label; unavailable days stay focusable (aria-disabled).
 */
import { h, clear } from './dom.js';
import { addDays, addMonths, daysInMonth, isMonth, monthOf, nightsBetween, shiftMonths, weekday, weekdayInitials } from './dates.js';

const KEYS = { ArrowLeft: -1, ArrowRight: 1, ArrowUp: -7, ArrowDown: 7 };

/**
 * Rooms' minimum stays for the months on screen (0.9.2), as the server combined them (Phase 3): one month →
 * `minStayByMonth[m]`, two months → `minStayByWindow[first]`; the whole window (`minStay`) when none is known.
 * The page picks a server range, never takes a min / max of rules.
 * @param {{minStay: ?{from: number, to: number}, minStayByMonth?: Object, minStayByWindow?: Object}} state
 * @param {string[]|null} months Visible months ("YYYY-MM"), null = all.
 */
export function visibleMinStay(state, months) {
	const range = !months || months.length === 0 ? null
		: months.length === 1 ? state.minStayByMonth?.[months[0]] : state.minStayByWindow?.[months[0]];
	return range || state.minStay || null;
}

/** Legend template: « Séjour minimum : %d nuits » when every room has the same minimum, else the range. */
export function minStayLegend(range) {
	if (!range || !Number.isInteger(range.from) || !Number.isInteger(range.to)) {
		return null;
	}
	return range.from === range.to
		? ['calendar.legendMinStayOne', [range.from], range.from]
		: ['calendar.legendMinStay', [range.from, range.to], range.to];
}

/**
 * @param {{booking: Object, i18n: Object, variant?: 'inline'|'popover'|'sheet', month?: ?string, onDone?: Function, onClose?: Function, top?: Node, months?: ?number}} opts
 *   month = the shortcode attribute mois ("YYYY-MM", else ignored);
 *   top = a node shown above the months (the guests, in the compact panel);
 *   months = months shown at once (default: 1 in the sheet, else 2); 1 = the inline calendar of the search bar's
 *   Dates panel on a phone (0.9.9).
 */
export function createCalendar({ booking, i18n, variant = 'inline', month: blockMonth = null, onDone = null, onClose = null, top = null, months: shown = null }) {
	const t = i18n.t;
	const state = booking.state;
	const visibleMonths = shown === 1 || variant === 'sheet' ? 1 : 2; // phone: one month at a time (0.9.5)
	/** First month when no arrival is chosen: URL mois > shortcode mois > the server's default month > today. */
	const firstMonth = () => [state.month, blockMonth, state.config?.calendar?.defaultMonth].find(isMonth) || monthOf(state.today);
	let cursor = state.arrival ? monthOf(state.arrival) : firstMonth(); // first month shown
	let placed = state.loaded; // the server's default month is known once /start has answered
	let moved = false; // the guest moved the calendar: keep it where it is
	let focusDate = null;
	let hover = null;
	let shownArrival = state.arrival;
	const uid = Math.random().toString(36).slice(2, 7); // unique month title ids per calendar

	const live = h('p', { class: 'pap-sr', 'aria-live': 'polite' });
	const step = h('p', { class: 'pap-cal-step' });
	const minLine = h('p', { class: 'pap-cal-min', hidden: true });
	const hint = h('p', { class: 'pap-cal-hint', 'aria-hidden': 'true' });
	const months = h('div', { class: 'pap-cal-months', on: { keydown: onKey, mouseover: onOver, mouseout: onOut, focusin: onOver, click: onClick } });
	const nav = h('div', { class: 'pap-cal-nav' });
	const body = h('div', { class: 'pap-cal-body' }, nav, months);
	const legend = h('div', { class: 'pap-cal-legend' });
	const head = h('div', { class: 'pap-cal-head' });
	const reset = variant === 'sheet' ? null : h('button', { type: 'button', class: 'pap-link pap-cal-reset', on: { click: () => booking.clearDates() } }, t('ui.reset'));
	const el = h('div', {
		class: 'pap-cal pap-cal--' + variant,
		...(variant === 'sheet' ? { role: 'dialog', 'aria-modal': 'true', 'aria-label': i18n.s('ui.dates'), on: { keydown: onEscape } } : {}),
		...(variant === 'popover' ? { role: 'dialog', 'aria-label': i18n.s('ui.dates'), on: { keydown: onEscape } } : {})
	}, head, top, live, step, minLine, body, hint, legend, reset ? h('div', { class: 'pap-cal-foot' }, reset) : null);

	// ------------------------------------------------------------ day state (from server data only)

	function mode() {
		return state.arrival && !state.departure ? 'departure' : 'arrival';
	}

	/** The minimum nights from the chosen arrival, once its departures are in (server data), else null. */
	function minNights() {
		return state.arrival && state.departuresFor === state.arrival && state.minNights && state.firstDeparture ? state.minNights : null;
	}

	/** A day between the arrival and its first departure: too short a stay (1.7.3). */
	function tooShort(date) {
		return mode() === 'departure' && minNights() !== null && date > state.arrival && date < state.firstDeparture;
	}

	/** {action, tip, cls, min} of a day; min = « min. N nuits » on the day after the arrival. */
	function day(date) {
		const flags = state.days[date];
		const action = booking.pickAction(date);
		const cls = ['pap-day'];
		let tip = null;
		let min = null;
		const outside = date < state.today || !state.maxDate || date > state.maxDate;
		if (outside) {
			cls.push('is-past');
		}
		const afterArrival = mode() === 'departure' && date > state.arrival;
		if (afterArrival) {
			if (tooShort(date)) {
				tip = ['calendar.MIN_NIGHTS', [state.minNights], state.minNights];
				if (date === addDays(state.arrival, 1)) {
					min = ['calendar.minNightsCell', [state.minNights], state.minNights];
					cls.push('has-min');
				}
			}
			if (action === 'departure') {
				cls.push('is-departure');
			}
		} else if (!outside && flags) {
			if (flags.closed) {
				cls.push('is-closed');
				tip = ['calendar.closed'];
			} else if (flags.reason && i18n.has('calendar.' + flags.reason)) {
				tip = ['calendar.' + flags.reason];
			}
		}
		if (date === state.arrival) {
			cls.push('is-start');
			if (!state.departure) {
				cls.push('is-pick'); // arrival alone: a ring
			}
		}
		if (date === state.departure) {
			cls.push('is-end');
		}
		if (state.arrival && state.departure && date > state.arrival && date < state.departure) {
			cls.push('is-range');
		}
		if (!action) {
			cls.push('is-off');
		}
		return { action, tip, cls, min };
	}

	const tipText = (tip) => (tip ? i18n.s(tip[0], tip[1] || [], tip[2] ?? null) : '');
	/** Shown on screen (browser tooltip, hint line): every tip but « N nuits minimum » — the minimum line and the
	 * « min. N nuits » label already say it (1.7.5); it stays in the accessible name. */
	const visibleTip = (tip) => (tip && tip[0] !== 'calendar.MIN_NIGHTS' ? tipText(tip) : '');

	/** Nights bubble above a departure (chosen or previewed). */
	function bubble(departure) {
		const n = nightsBetween(state.arrival, departure);
		return h('span', { class: 'pap-day-tip', 'aria-hidden': 'true' }, t('ui.nights', [n], n));
	}

	// ------------------------------------------------------------ rendering

	function dayButton(date) {
		const d = day(date);
		const selected = date === state.arrival || date === state.departure;
		const role = date === state.arrival ? i18n.s('ui.arrivalChosen') : date === state.departure ? i18n.s('ui.departureChosen') : '';
		const parts = [i18n.date(date, 'aria'), role, tipText(d.tip)].filter(Boolean);
		const label = parts.length === 1 ? parts[0] : i18n.s(parts.length === 2 ? 'ui.ariaPair' : 'ui.ariaTriple', parts); // each language its separator
		return h('button', {
			type: 'button',
			role: 'gridcell',
			class: d.cls.join(' '),
			'data-date': date,
			tabindex: date === focusDate ? '0' : '-1',
			'aria-label': label,
			'aria-disabled': d.action ? null : 'true',
			'aria-selected': selected ? 'true' : 'false',
			title: visibleTip(d.tip) || null
		},
		date === state.departure && state.arrival ? bubble(date) : null,
		d.min ? h('span', { class: 'pap-day-min', 'aria-hidden': 'true' }, t(d.min[0], d.min[1], d.min[2])) : null,
		h('span', { class: 'pap-day-n' }, i18n.value(i18n.date(date, 'dayNumber'))));
	}

	/** Weekday initials from Intl in the page language (« lu ma me… », « Mo Tu We… »), never translated. */
	const weekdays = weekdayInitials(i18n.lang);
	function weekdayCells() {
		return weekdays.map((name) => h('span', { class: 'pap-wd', role: 'columnheader', 'aria-hidden': 'true' }, i18n.value(name)));
	}

	function month(m) {
		const first = m + '-01';
		const title = h('h3', { class: 'pap-cal-title', id: 'pap-m-' + m + '-' + uid }, i18n.value(i18n.date(first, 'month')));
		const grid = h('div', { class: 'pap-grid', role: 'grid', 'aria-labelledby': title.id });
		grid.append(h('div', { class: 'pap-week pap-wd-row', role: 'row' }, ...weekdayCells()));
		let row = h('div', { class: 'pap-week', role: 'row' });
		for (let i = 0; i < weekday(first); i++) {
			row.append(h('span', { class: 'pap-pad', role: 'gridcell', 'aria-hidden': 'true' }));
		}
		for (let d = 1; d <= daysInMonth(m); d++) {
			const date = `${m}-${String(d).padStart(2, '0')}`;
			row.append(dayButton(date));
			if (weekday(date) === 6) {
				grid.append(row);
				row = h('div', { class: 'pap-week', role: 'row' });
			}
		}
		if (row.childNodes.length) {
			grid.append(row);
		}
		return h('div', { class: 'pap-month', 'data-month': m }, title, grid);
	}

	function monthList() {
		const out = [];
		for (let i = 0, m = cursor; i < visibleMonths; i++, m = monthOf(addMonths(m + '-01', 1))) {
			out.push(m);
		}
		return out;
	}

	function clampCursor() {
		const min = monthOf(state.today);
		// Before /calendar answers there is no window end yet (Phase 3: the server's `to`): only the lower bound.
		const max = state.maxDate ? monthOf(addMonths(state.maxDate, -(visibleMonths - 1))) : null;
		cursor = cursor < min ? min : max && cursor > max ? max : cursor;
	}

	function defaultFocus() {
		const list = monthList();
		const candidates = [state.departure, state.arrival].filter(Boolean);
		const visible = candidates.find((d) => list.includes(monthOf(d)));
		if (visible) {
			return visible;
		}
		const firstMonth = list[0] + '-01';
		return firstMonth < state.today ? state.today : firstMonth;
	}

	function renderHead() {
		clear(head);
		if (variant === 'inline') {
			return;
		}
		const value = (iso) => (iso ? i18n.value(i18n.date(iso, 'short')) : i18n.value('—'));
		head.append(
			h('div', { class: 'pap-cal-sel' },
				h('span', { class: 'pap-cal-sel-d' }, h('small', {}, t('ui.arrival')), value(state.arrival)),
				h('span', { class: 'pap-cal-sel-d' }, h('small', {}, t('ui.departure')), value(state.departure))
			),
			h('div', { class: 'pap-cal-actions' },
				// The sheet keeps its header button; the popover has « Réinitialiser » under the months.
				variant === 'sheet' && state.arrival ? h('button', { type: 'button', class: 'pap-link', on: { click: () => booking.clearDates() } }, t('ui.clearDates')) : null,
				onClose ? h('button', { type: 'button', class: 'pap-btn pap-btn-ghost pap-cal-close', on: { click: onClose } }, t('ui.close')) : null,
				onDone ? h('button', { type: 'button', class: 'pap-btn pap-cal-done', on: { click: onDone } }, t('ui.validate')) : null
			)
		);
	}

	function renderNav() {
		clear(nav);
		const canPrev = cursor > monthOf(state.today);
		const canNext = Boolean(state.maxDate) && monthOf(addMonths(cursor + '-01', visibleMonths)) <= monthOf(state.maxDate);
		nav.append(
			h('button', { type: 'button', class: 'pap-nav pap-nav-prev', 'aria-label': i18n.s('ui.prevMonth'), disabled: !canPrev, on: { click: () => { booking.calendarUsed(); moved = true; cursor = monthOf(addMonths(cursor + '-01', -1)); focusDate = null; render(); } } }, '‹'),
			h('button', { type: 'button', class: 'pap-nav pap-nav-next', 'aria-label': i18n.s('ui.nextMonth'), disabled: !canNext, on: { click: () => { booking.calendarUsed(); moved = true; cursor = monthOf(addMonths(cursor + '-01', 1)); focusDate = null; render(); } } }, '›')
		);
	}

	/** « Séjour minimum : N nuits » once an arrival is chosen (1.7.3): the server's minimum for that arrival. */
	function renderMin() {
		const n = minNights();
		minLine.hidden = n === null;
		minLine.classList.remove('is-flash');
		clear(minLine);
		if (n !== null) {
			minLine.append(t('calendar.legendMinStayOne', [n], n));
		}
	}

	/** A click on a day before the first departure: repeat the minimum (screen readers too), nothing else. */
	function remindMin() {
		const n = minNights();
		clear(live).append(t('calendar.legendMinStayOne', [n], n));
		minLine.classList.remove('is-flash');
		void minLine.offsetWidth; // restart the animation
		minLine.classList.add('is-flash');
	}

	/** Legend: one struck sample day for every unselectable day, and the rooms' minimum stays from the server. */
	function renderLegend() {
		clear(legend);
		legend.append(h('span', { class: 'pap-cal-legend-off' },
			h('s', { class: 'pap-cal-legend-day', 'aria-hidden': 'true' }, i18n.value(i18n.date('2027-03-12', 'dayNumber'))),
			i18n.sep(' '),
			t('calendar.legendOff')));
		// Once an arrival is chosen the minimum line gives its own minimum: no range beside it (1.7.5).
		const range = minNights() === null ? visibleMinStay(state, monthList()) : null;
		const text = minStayLegend(range);
		if (text) {
			legend.append(h('span', { class: 'pap-cal-legend-min' }, t(text[0], text[1], text[2])));
		}
	}

	function render() {
		const hadFocus = months.contains(document.activeElement);
		hover = null;
		// An arrival set from outside (URL, suggestion) brings its month into view; a click in the grid does not move it.
		if (!placed && state.loaded) {
			placed = true;
			if (!moved && !state.arrival) {
				cursor = firstMonth();
				focusDate = null;
			}
		}
		if (state.arrival !== shownArrival) {
			shownArrival = state.arrival;
			if (state.arrival && !monthList().includes(monthOf(state.arrival))) {
				cursor = monthOf(state.arrival);
				focusDate = null;
			}
		}
		clampCursor();
		const list = monthList();
		if (!focusDate || !list.includes(monthOf(focusDate))) {
			focusDate = defaultFocus();
		}
		el.classList.toggle('is-loading', !state.loaded);
		el.setAttribute('aria-busy', state.loaded ? 'false' : 'true');
		const instruction = state.loaded ? (mode() === 'departure' ? 'ui.chooseDeparture' : 'ui.chooseArrival') : 'ui.loading';
		clear(step).append(t(instruction));
		if (live.textContent !== i18n.s(instruction)) {
			clear(live).append(t(instruction));
		}
		renderHead();
		renderMin();
		renderNav();
		clear(months);
		for (const m of list) {
			months.append(month(m));
		}
		renderLegend();
		if (reset) {
			reset.disabled = !state.arrival;
		}
		if (hadFocus) {
			button(focusDate)?.focus();
		}
	}

	const button = (date) => months.querySelector(`[data-date="${date}"]`);

	// ------------------------------------------------------------ interaction

	function moveFocus(date) {
		const min = state.today;
		const max = state.maxDate;
		focusDate = date < min ? min : max && date > max ? max : date;
		if (!monthList().includes(monthOf(focusDate))) {
			moved = true;
			cursor = focusDate > cursor + '-01' ? monthOf(addMonths(focusDate, -(visibleMonths - 1))) : monthOf(focusDate);
			render();
		} else {
			for (const b of months.querySelectorAll('[data-date]')) {
				b.tabIndex = b.dataset.date === focusDate ? 0 : -1;
			}
		}
		const b = button(focusDate);
		b?.focus();
		b?.scrollIntoView?.({ block: 'nearest' });
	}

	function onKey(e) {
		const date = e.target?.dataset?.date;
		if (!date) {
			return;
		}
		let next = null;
		if (e.key in KEYS) {
			next = addDays(date, KEYS[e.key]);
		} else if (e.key === 'PageUp' || e.key === 'PageDown') {
			next = shiftMonths(date, e.key === 'PageUp' ? -1 : 1);
		} else if (e.key === 'Home') {
			next = addDays(date, -weekday(date));
		} else if (e.key === 'End') {
			next = addDays(date, 6 - weekday(date));
		} else if (e.key === 'Enter' || e.key === ' ') {
			e.preventDefault();
			select(date);
			return;
		} else {
			return;
		}
		e.preventDefault();
		moveFocus(next);
		showHint(next);
	}

	function onClick(e) {
		const b = e.target.closest?.('[data-date]');
		if (b) {
			select(b.dataset.date);
		}
	}

	function select(date) {
		booking.calendarUsed(); // Matomo `calendrier-ouvert` (1.8.0): a day clicked or chosen with Enter / Space
		focusDate = date;
		if (booking.pickAction(date)) {
			booking.pick(date);
		} else if (tooShort(date)) {
			remindMin();
		}
	}

	function onEscape(e) {
		if (e.key === 'Escape' && onClose) {
			e.preventDefault();
			onClose();
		}
	}

	function showHint(date) {
		const tip = date ? day(date).tip : null;
		clear(hint).append(visibleTip(tip)); // not textContent: a replaced text node would reach TranslatePress unparented (dom.js clear())
	}

	/**
	 * Range preview towards a valid departure (hover or focus): band on the nights, filled circle and nights
	 * bubble on the departure, the arrival's ring becomes a filled circle. Classes only: no re-render.
	 */
	function preview(target) {
		if (target === hover) {
			return;
		}
		hover = target;
		months.querySelector('.pap-day-tip.is-preview-tip')?.remove();
		for (const d of months.querySelectorAll('[data-date]')) {
			const date = d.dataset.date;
			d.classList.toggle('is-preview', Boolean(hover) && date > state.arrival && date < hover);
			d.classList.toggle('is-preview-end', date === hover);
			if (date === state.arrival) {
				d.classList.toggle('is-pick', !hover && !state.departure);
			}
		}
		if (hover) {
			const tip = bubble(hover);
			tip.classList.add('is-preview-tip');
			button(hover)?.prepend(tip);
		}
	}

	/** Hover / focus: hint line, and the range preview towards a valid departure. */
	function onOver(e) {
		const b = e.target.closest?.('[data-date]');
		if (!b) {
			return;
		}
		showHint(b.dataset.date);
		// « min. N nuits » hidden while the pointer (or focus) is on another day than it and the arrival (1.7.4).
		months.classList.toggle('is-elsewhere', !b.classList.contains('has-min') && b.dataset.date !== state.arrival);
		preview(mode() === 'departure' && booking.pickAction(b.dataset.date) === 'departure' ? b.dataset.date : null);
	}

	function onOut(e) {
		if (!months.contains(e.relatedTarget)) {
			months.classList.remove('is-elsewhere');
			clear(hint);
			preview(null);
		}
	}

	return {
		el,
		update: render,
		/** Focus the current day (opening the sheet / popover, or keyboard entry). */
		focus() {
			render();
			button(focusDate)?.focus();
		},
		get focusDate() {
			return focusDate;
		}
	};
}
